import { useState, useRef, memo, useEffect } from 'react';
import QaSkeleton from '@/sub-luca-plugin/components/qa-skeleton';
import NavigationHeaderWapper from '@/sub-luca-plugin/components/navigation-header-wapper';
import {
    luca_ask_assign,
    luca_ask_detail,
    luca_ask_problem_summary_config,
    feedback_label_list,
    query_luca_ask_chat_id_by_resource_id,
} from '@/sub-luca-plugin/apis/qa';
import { qa_detail_by_ask_id, teacher_reply_time } from '@/sub-luca-plugin/apis';
import { getQaStore } from '@/sub-luca-plugin/store/qa';
import {
    createQaRecordId,
    transformToQaRecordsByOldQaDetail,
    transformToQuestionSourceRecordBySource,
    transformToQaRecords,
} from '@/sub-luca-plugin/utils/qa';
import { getBotKey, excuteEveryoneIsAskProcess, excuteInitGuessYouAskProcess } from './index.helper';
import { getUuid } from '@/sub-luca-plugin/utils';
import { eventBusExample } from '@/sub-luca-plugin/utils/class-example';
import ProjectSelectOverlay from '@/sub-luca-plugin/components/project-select-overlay';
import { IQuestionState, IProjectConfig, INoticeData, IProjectIds, TResourceType } from '@/sub-luca-plugin/types';
import { IQaRecordItem } from '../qa-records';
import { queryProjects, IReturnConfig } from '@/sub-luca-plugin/components/project-select-overlay/helper';
import './index.scss';

const QaInitOverlay = () => {
    const lucaIdRef = useRef<number>();
    const callbacksRef = useRef<Record<string, (data: any) => void>>({});
    const showProjectSelectCloseBtn = useRef(false);
    const [status, setStatus] = useState<'init' | 'hide' | 'select' | 'error'>('hide');
    const [projects, setProjects] = useState<IProjectConfig[]>([]);
    const [determinedIds, setDeterminedIds] = useState<IProjectIds>({});
    const [allowChangeDeterminedIds, setAllowChangeDeterminedIds] = useState(false);

    // 获取AI回复内容的反馈选择列表
    const getFeedbackLabelList = async () => {
        const { result } = await feedback_label_list();
        getQaStore()?.updateQaConfig({ feedbackLabelList: result || [] });
    };

    // 分配AI答疑 / 非AI答疑
    const lucaAskAssign = async ({ lucaId, projectId, subjectId, twoLevelSubjectId }: any) => {
        const { qaBasicConfig } = getQaStore()!;
        const { result } = await luca_ask_assign({
            lucaId,
            projectId,
            subjectId,
            twoLevelSubjectId,
            scenario: qaBasicConfig.current.resourceType === 2 ? 1 : 0,
        });
        return result;
    };

    // 初始化答疑配置
    const initQa = async (config: IReturnConfig) => {
        setStatus('init');
        const { qaBasicConfig, setQaRecords, updateQaConfig, openExcuteTimer, getExcuteProcessId, isContinueExcute, deleteExcuteRecord } =
            getQaStore()!;
        const newConfig = {
            ...config,
            lucaId: lucaIdRef.current,
        };
        const assignInfo = await lucaAskAssign(newConfig);
        if (!assignInfo) {
            setStatus('error');
            return;
        }
        const { initResourceType, initResourceId } = qaBasicConfig.current;
        const records: IQaRecordItem[] = [];
        // 首页OCR执行流程，不要欢迎语(非AI模式除外)
        if (!callbacksRef.current.startCombineSearch || assignInfo.aiTag !== 1) {
            records.push({
                recordId: createQaRecordId('respondent'),
                role: 'respondent',
                type: 'welcome',
                content: initResourceType === 2 ? config.paperWelcomeDoc || config.welcomeDoc : config.welcomeDoc,
            });
        }
        const excuteId = getExcuteProcessId();
        openExcuteTimer();
        const everyoneIsAskProcessFn = excuteEveryoneIsAskProcess({ initResourceType, initResourceId });
        const guessYouAskProcessFn = excuteInitGuessYouAskProcess({
            ...newConfig,
            initResourceType,
            initResourceId,
            aiTag: assignInfo.aiTag,
        });
        setQaRecords(records);
        const [questionBotKey, botKey] = await Promise.all([getBotKey(newConfig.questionBotId), getBotKey(assignInfo.botId)]);
        updateQaConfig(
            {
                ...assignInfo,
                ...newConfig,
                // ai答疑模式且资源类型不等于题目展示
                showSearchQuestionsByOcrBtn: assignInfo.aiTag === 1 && initResourceType !== 2,
                showSubjectSearchLabel: assignInfo.aiTag === 1 && newConfig.subjectSearchOnOff,
                questionBotKey,
                botKey,
                refreshCommRecordsByEffect: getUuid(),
                canExcuteAimation: true,
            },
            true
        );
        await callbacksRef.current.startCombineSearch?.(assignInfo.aiTag === 1);
        setStatus('hide');
        isContinueExcute(excuteId) && (await everyoneIsAskProcessFn?.());
        isContinueExcute(excuteId) && (await guessYouAskProcessFn?.());
        deleteExcuteRecord(excuteId);
        getFeedbackLabelList();
        callbacksRef.current = {};
    };

    // 通过lucaAskChatId初始化
    const initByLucaAskChatId = async ({ lucaAskChatId }: { lucaAskChatId: string }) => {
        setStatus('init');
        const { setQaRecords, updateQaConfig } = getQaStore()!;
        const { result } = await luca_ask_detail({ lucaAskChatId });
        if (!result) {
            setStatus('error');
            return;
        }
        const { askChatRecords = [], source, permission } = result || {};
        const config: any = {
            ...result,
            lucaQuestionAgainNum: result.residueLucaAgainNum,
            teacherQuestionAgainNum: result.residueTeacherAgainNum,
        };
        const { result: summaryResult } = await luca_ask_problem_summary_config({
            lucaId: result.lucaId,
            projectId: result.projectId,
            subjectId: result.subjectId,
            twoLevelSubjectId: result.twoLevelSubjectId,
        });
        const [questionBotKey, botKey] = await Promise.all([getBotKey(summaryResult?.botId), getBotKey(result.botId)]);
        const { records } = transformToQaRecords(askChatRecords?.reverse() || [], {
            whetherSolveTitle: result.tipsContent,
        });
        const questionSourceRecord = transformToQuestionSourceRecordBySource({ ...(source || {}), permission });
        if (questionSourceRecord) records.unshift(questionSourceRecord);
        if (config.transferLaborState === 0 && [0, 1, 3].includes(config.questionState) && config.lucaQuestionAgainNum === 0) {
            records.push({
                recordId: createQaRecordId('respondent'),
                role: 'respondent',
                type: 'qaNumUsedUp',
                content: '你的追问次数已用完，若还未解决你的疑问，请点击下方【未解决】按钮，将问题提交给名师回答',
            });
        } else if (config.questionState === 5 && config.appraiseState === 0 && config.teacherQuestionAgainNum === 0) {
            records.push({
                recordId: createQaRecordId('respondent'),
                role: 'respondent',
                type: 'qaNumUsedUp',
                content: '你的追问次数已用完，请对老师的回答进行满意度评价~',
            });
        }
        if (config.questionState === 3) {
            records.push({
                recordId: createQaRecordId('system'),
                role: 'system',
                type: 'whetherSolve',
                state: 3,
                title: result.tipsContent,
            });
            records.push({
                recordId: createQaRecordId('respondent'),
                role: 'respondent',
                type: 'goToTeacherQaTips',
                content: '很抱歉，未能解决你的疑问。你可以将问题提交给名师，获得专业回答',
            });
            records.push({
                recordId: createQaRecordId('respondent'),
                role: 'respondent',
                type: 'goToTeacherQa',
                teacherAnswerTime: result.teacherAnswerTime,
            });
        }
        delete config.askChatRecords;
        delete config.residueLucaAgainNum;
        delete config.residueTeacherAgainNum;
        updateQaConfig(
            {
                ...config,
                questionBotId: summaryResult?.botId,
                // display，用于控制拍题答疑后不展示是否解决卡片
                showWhetherSolve: result.display && [0, 1].includes(result.questionState),
                showSearchQuestionsByOcrBtn:
                    config.aiTag === 1 &&
                    config.resourceType !== 2 &&
                    config.imageFlag !== 1 &&
                    config.transferLaborState !== 1 &&
                    config.questionState !== 2,
                showSubjectSearchLabel:
                    config.aiTag === 1 && config.subjectSearchOnOff && config.transferLaborState !== 1 && config.questionState !== 2,
                questionBotKey,
                botKey,
                refreshCommRecordsByEffect: getUuid(),
            },
            true
        );
        setQaRecords(records);
        setStatus('hide');
        setTimeout(() => {
            updateQaConfig({ canExcuteAimation: true });
        }, 100);
        getFeedbackLabelList();
    };

    // 通过AskId初始化
    const initByAskId = async ({ askId }: { askId: number }) => {
        setStatus('init');
        const { result } = await qa_detail_by_ask_id(askId);
        const { result: timeResult } = await teacher_reply_time({ subjectId: result.subjectId });
        const { setQaRecords, updateQaConfig } = getQaStore()!;
        const { answer } = result;
        let questionState: IQuestionState = 4;
        if (answer?.length && (answer[answer.length - 1].answer_content || answer[answer.length - 1].voice_list.length > 0)) {
            questionState = 5;
        }
        updateQaConfig(
            {
                id: result.id,
                askId,
                projectId: result.projectId,
                projectName: result.projectName,
                subjectId: result.subjectId,
                subjectName: result.subjectName,
                twoLevelSubjectId: result.twoLevelSubjectId,
                twoLevelSubjectName: result.twoLevelSubjectName,
                askChatTitle: answer[0]?.question_content,
                transferLaborState: 1,
                questionState,
                teacherQuestionAgainNum: result.allowTraceTimes,
                teacherAnswerTime: timeResult?.answerTime || 0,
                appraiseState: result.appraisal?.appraisal_id ? 1 : 0,
                appraise: result.appraisal
                    ? {
                          starLevel: result.appraisal.star,
                          content: result.appraisal.content,
                      }
                    : undefined,
                refreshCommRecordsByEffect: getUuid(),
                videoTime: result.videoTime,
                courseId: result.courseId,
                resourceId: result.resourceId,
                resourceType: result.resourceType,
                collectFlag: result.askCollect?.is_ask_collect === 1 ? true : false,
            },
            true
        );
        setQaRecords(transformToQaRecordsByOldQaDetail(result));
        setStatus('hide');
        setTimeout(() => {
            updateQaConfig({ canExcuteAimation: true });
        }, 100);
        getFeedbackLabelList();
    };

    // 通过resourceId初始化
    const initByResourceId = async ({
        lucaId,
        resourceType,
        resourceId,
        defaultIds,
    }: {
        resourceType: TResourceType;
        resourceId: number;
        lucaId: number;
        defaultIds: number[];
    }) => {
        setStatus('init');
        const { result } = await query_luca_ask_chat_id_by_resource_id({ resourceType, resourceId });
        setStatus('hide');
        result ? initByLucaAskChatId({ lucaAskChatId: result }) : initByLucaId({ lucaId, defaultIds });
    };

    // 通过lucaId初始化
    const initByLucaId = async ({
        lucaId = 0,
        defaultIds = [],
        allowChangeDeterminedIds: allow = false,
    }: {
        lucaId: number;
        defaultIds?: number[];
        allowChangeDeterminedIds?: boolean;
    }) => {
        lucaIdRef.current = lucaId;
        if (projects.length) {
            setStatus('select');
            setDeterminedIds({
                projectId: defaultIds[0],
                subjectId: defaultIds[1],
                twoLevelSubjectId: defaultIds[2],
            });
            setAllowChangeDeterminedIds(allow);
            return;
        }
        setStatus('init');
        const list = await queryProjects(lucaId);
        if (list.length) {
            setStatus('select');
            setProjects(list);
            setDeterminedIds({
                projectId: defaultIds[0],
                subjectId: defaultIds[1],
                twoLevelSubjectId: defaultIds[2],
            });
            setAllowChangeDeterminedIds(allow);
        } else {
            setStatus('error');
        }
    };

    const handleCloseProjectSubjectSelect = () => {
        showProjectSelectCloseBtn.current = false;
        setStatus('hide');
    };

    useEffect(() => {
        eventBusExample.subscribe('QaInitOverlay', ({ type, params = {}, callbacks }: INoticeData) => {
            switch (type) {
                case 'initByLucaAskChatId':
                case 'initByAskId':
                case 'initByResourceId':
                case 'initByLucaId':
                    showProjectSelectCloseBtn.current = params.showProjectSelectCloseBtn;
                    callbacksRef.current = callbacks || {};
                    getQaStore()!.interruptAllExcute('send_cancel');
                    getQaStore()!.clearQaStore();
                    eventBusExample.publish('NavgationBar', { type: 'reset' });
                    eventBusExample.publish('DialogueArea', { type: 'reset' });
                    eventBusExample.publish('DialogueTool', { type: 'reset' });
                    eventBusExample.publish('QaToast', { type: 'reset' });
                    ((initFnMapping, type) => {
                        initFnMapping[type](params as any);
                    })(
                        {
                            initByLucaAskChatId,
                            initByAskId,
                            initByResourceId,
                            initByLucaId,
                        },
                        type
                    );
                    break;
                default:
                    setStatus('hide');
                    setDeterminedIds({});
                    setAllowChangeDeterminedIds(false);
                    lucaIdRef.current = undefined;
                    callbacksRef.current = {};
                    break;
            }
        });
    }, [projects]);

    return status === 'init' ? (
        <QaSkeleton />
    ) : status === 'select' ? (
        <ProjectSelectOverlay
            overlayClassName="init-select-overlay"
            className="init-select"
            projects={projects}
            determinedIds={determinedIds}
            allowChangeDeterminedIds={allowChangeDeterminedIds}
            onClose={showProjectSelectCloseBtn.current ? handleCloseProjectSubjectSelect : undefined}
            onReturnConfig={initQa}
        />
    ) : status === 'error' ? (
        <div className="init-fail">
            <NavigationHeaderWapper className="init-fail__header">
                <div className="init-fail__header-avatar"></div>
                <span>Luca答疑</span>
            </NavigationHeaderWapper>
            <div className="init-fail__content-container">
                <div className="init-fail__content">服务异常</div>
            </div>
        </div>
    ) : null;
};

export default memo(QaInitOverlay);
